// ============================================================================
// Porte de validation d'un modèle de feuille de brouillon.
// ============================================================================
// Un modèle produit par la génération (IA, hors ligne) n'est enregistrable que
// s'il passe cette porte. Elle rejoue le moteur sur le modèle, sans IA :
//   1. le modèle respecte le schéma et toutes ses lignes se lisent ;
//   2. les égalités de l'énoncé sont vraies, les pièges sont faux ;
//   3. le solveur atteint le but (il existe un corrigé ligne à ligne) ;
//   4. chaque ligne de ce corrigé est acceptée par le juge ;
//   5. taper chaque ligne bout par bout ne passe jamais au rouge ;
//   6. la réponse ne s'écrit pas directement, sans raisonnement — sauf si cette
//      ligne unique vient d'un outil de carte (reconnaître une identité
//      remarquable, distribuer, appliquer l'outil ou la formule de la SKILLCARD) ;
//   7. les choix du QCM sont cohérents avec le modèle (bonne réponse vraie,
//      distracteurs faux) ;
//   8. le temps de jugement reste dans le budget.
// Le rapport est stocké avec le modèle (`scratch_models.validation`).
// ============================================================================

import { isSignTableText } from './kernel-signs';
import {
  createKVarState,
  kvarLineAtoms,
  VariationsModelError,
  type KVarState,
} from './kernel-variations';
import { createKTabState, ktabLineAtoms, TablesModelError, type KTabState } from './kernel-tables';
import {
  createKLcState,
  klcHolds as klcHoldsText,
  LimitClaimsModelError,
  type KLcState,
} from './kernel-limit-claims';
import {
  createKRiemState,
  kriemCheckLimit,
  kriemHolds,
  kriemIsAnswer,
  kriemLineAtoms,
} from './kernel-riemann';
import { type Chip, formatNumber, lex, normalize } from './lexer';
import {
  ENGINE_VERSION,
  KERNEL_POWER_ENGINE_VERSION,
  ScratchModelSchema,
  kernelSpecialPlugin,
  modelEngineVersion,
  usesKernelPoly,
  usesKernelTrig,
  type ChoiceSpec,
  type ScratchModel,
} from './model';
import {
  SKILLS,
  commit,
  createState,
  goalReached,
  isAnswer,
  isTrue,
  judge,
  solve,
  type VerdictKind,
} from './reasoner';
import { materializeModel } from './samples';
import { usesNamedProducts } from './named-products';
import { complexEquals, complexValueOf, cxText } from './complex';
import { close, lineAtoms, parseText, roundTo } from './tree';
import { parseUnitsText, unitsIsTrue, unitsLineToLine } from './units';
import { geoChipsOf, parseProp, propKey } from './geometry';
import {
  kernelAnswerContains,
  kernelEquivalentToAnswer,
  kernelPathEquivalent,
  kernelConditionFollows,
  kernelAnswerFollowsWithConditions,
  kernelLineAtoms,
  kernelSystemFollows,
} from './kernel-sheet';
import { kgeoHolds, kgeoLineAtoms, kgeoReadFact, kgeoSameAsAnswer } from './kernel-geometry';
import {
  createKRecState,
  krecLineAtoms,
  krecReadFact,
  krecSameAsAnswer,
  krecTruth,
} from './kernel-recurrence';
import {
  createKProgState,
  kprogCodeAtoms,
  kprogEverHolds,
  kprogHolds,
  kprogLineAtoms,
  kprogSameAsAnswer,
  type KProgState,
} from './kernel-python';
import {
  createKMatState,
  kmatHolds,
  kmatIsAnswer,
  kmatJudge,
  kmatLineAtoms,
} from './kernel-matrix';
import { parseMatLine } from './kernel/matrix';
import {
  arithQuestionOf,
  createKArithState,
  karithHolds,
  karithIsAnswer,
  karithJudge,
  karithLineAtoms,
} from './kernel-arith';
import { createKLimState, klimHolds, klimIsAnswer, klimLineAtoms } from './kernel-limit';
import { createKIntState, kintHolds, kintIsAnswer, kintLineAtoms } from './kernel-integral';
import { createKOdeState, kodeHolds, kodeIsAnswer, kodeLineAtoms } from './kernel-ode';
import { createKPartState, kpartHolds, kpartIsAnswer, kpartLineAtoms } from './kernel-partial';
import { sameLimit, trueLimit } from './kernel/limit';
import { euclid, parseArithLine } from './kernel/arith';
import {
  ClaimsModelError,
  createKClaimsState,
  kclaimsHolds,
  kclaimsLineAtoms,
} from './kernel-claims';
import { kforReadAtoms } from './kernel-formula';
import {
  createKAnaState,
  kanaHolds,
  kanaIsAnswer,
  kanaJudge,
  kanaLineAtoms,
} from './kernel-analysis';
import {
  createKForState,
  kforHolds,
  kforIsAnswer,
  kforJudge,
  kforLineAtoms,
} from './kernel-formula';
import { linearForm } from './kernel/system';
import { systemOutcomeOf } from './kernel-system';
import { parseStatement, relationsIn, statementSymbols, symbolsOf } from './kernel/term';
import { approximationOf } from './kernel/legacy';
import { relForm } from './kernel-poly';
import { trigDomainOf, trigNodes } from './kernel/trig';

/** Un choix du QCM à confronter au modèle (même forme que `ChoiceSpec`). */
export type ChoiceToCheck = ChoiceSpec;

export type ValidationIssueCode =
  | 'schema'
  | 'unparsable_line'
  | 'given_false'
  | 'trap_true'
  | 'unsolvable'
  | 'path_line_rejected'
  | 'red_while_typing'
  | 'answer_direct'
  | 'choice_inconsistent'
  | 'too_slow'
  | 'no_steps';

export interface ValidationIssue {
  code: ValidationIssueCode;
  message: string;
  line?: string;
}

export interface ValidationReport {
  ok: boolean;
  engine_version: number;
  issues: ValidationIssue[];
  /** corrigé trouvé par le moteur (feuille du noyau : avec la règle de chaque pas) */
  solution: { text: string; label: string; hora: number; rule?: string }[];
  reached: boolean;
  /** HORA du corrigé : sert de plafond par question côté serveur */
  max_hora: number;
  /** jugement le plus lent mesuré pendant la validation (ms) */
  max_judge_ms: number;
  /** distracteurs reconnus comme pièges dès le départ (information, non bloquant) */
  distractors_as_traps: number;
  /**
   * La réponse s'écrit en une ligne, justifiée par un outil de carte (ex. « Identité (a + b)² ») :
   * le nom de cet outil. La question reste ouverte : la feuille juge la reconnaissance et ses pièges.
   */
  direct_tool: string | null;
}

export interface ValidateOptions {
  /** choix du QCM ; par défaut, ceux du modèle (`model.choices`) */
  choices?: ChoiceToCheck[];
  /** budget par jugement (ms) ; au-delà, l'issue `too_slow` est levée */
  budgetMs?: number;
  /** chemin préféré du solveur (ex. « Coefficient multiplicateur ») */
  prefer?: string | null;
}

/** outils de carte : une réponse en une ligne qu'ils justifient reste une question ouverte */
const CARD_TOOLS = new Set<string>([
  ...Object.keys(SKILLS),
  'identite',
  'distributivite',
  // congruences : appliquer le petit théorème de Fermat est l'outil de la carte
  'fermat',
]);

const RED: Record<string, 1> = { trap: 1, syntax: 1, false: 1, unjustified: 1, useless: 1 };
const OPERATOR = /^[=+−×÷(]$/;

function now(): number {
  const p = (globalThis as { performance?: { now(): number } }).performance;
  return p ? p.now() : Date.now();
}
function chipsOf(atoms: string[]): Chip[] {
  return atoms.map((a) => ({ atoms: [a], text: a }));
}

/** Rejoue le moteur sur un modèle. Ne lance jamais : tout problème devient une issue du rapport. */
export function validateModel(input: unknown, opts: ValidateOptions = {}): ValidationReport {
  const report: ValidationReport = {
    ok: false,
    engine_version: ENGINE_VERSION,
    issues: [],
    solution: [],
    reached: false,
    max_hora: 0,
    max_judge_ms: 0,
    distractors_as_traps: 0,
    direct_tool: null,
  };
  const issue = (code: ValidationIssueCode, message: string, line?: string): void => {
    report.issues.push(line === undefined ? { code, message } : { code, message, line });
  };

  // 1. schéma
  const parsed = ScratchModelSchema.safeParse(input);
  if (!parsed.success) {
    parsed.error.issues.forEach((i) => issue('schema', i.path.join('.') + ' : ' + i.message));
    return report;
  }
  // la version se lit sur le modèle TEL QU'ÉCRIT : « 2ab » (lu a × b, moteur v41) est
  // réécrit « 2a b » à la lecture, ce qu'une app plus ancienne ne fait pas
  report.engine_version = Math.max(
    modelEngineVersion(parsed.data),
    rawUsesNamedProducts(input) ? KERNEL_POWER_ENGINE_VERSION : 0,
  );
  let model: ScratchModel;
  try {
    model = materializeModel(parsed.data);
  } catch (e) {
    issue('schema', 'samples : ' + (e instanceof Error ? e.message : String(e)));
    return report;
  }

  // mode spécial passé au noyau (moteur v34) : la porte du mode (même juge, règles du
  // noyau), puis le départ, la réponse et le corrigé de `kernel`
  const special = kernelSpecialPlugin(model);
  if (special) {
    checkKernelSpecial(model, special, issue);
    if (report.issues.length) return report;
  }
  // feuille jugée par le noyau (moteur v18) : porte dédiée
  if (model.kernel && !special) {
    validateKernel(model, report, issue, opts);
    report.ok = report.issues.length === 0;
    return report;
  }
  if (special) {
    const finish = (): ValidationReport => {
      if (!report.issues.length) checkKernelSpecialAnswer(model, issue);
      report.ok = report.issues.length === 0;
      return report;
    };
    if (model.bounds) {
      validateBounds(model, report, issue);
      return finish();
    }
    if (model.units) {
      validateUnits(model, report, issue, opts);
      return finish();
    }
    // congruences et complexes : la porte générale ci-dessous, puis la réponse
  }
  // encadrer une racine carrée (moteur v13) : porte dédiée
  if (model.bounds) {
    validateBounds(model, report, issue);
    report.ok = report.issues.length === 0;
    return report;
  }
  // raisonnement géométrique (moteur v4) : porte dédiée
  if (model.geometry) {
    validateGeometry(model, report, issue, opts);
    report.ok = report.issues.length === 0;
    return report;
  }
  // grandeurs et unités (moteur v4) : porte dédiée
  if (model.units) {
    validateUnits(model, report, issue, opts);
    report.ok = report.issues.length === 0;
    return report;
  }

  // 1 bis. toutes les lignes se lisent
  const texts: string[] = [
    ...(model.givens || []),
    ...(model.admitted || []),
    ...(model.answers || []),
    ...(model.traps || []).map((t) => t.line),
  ];
  (model.choices || []).forEach((c) => {
    if (c.line) texts.push(c.line);
  });
  (model.preds || []).forEach((p) => {
    if (p.skill === 'formule') texts.push(p.line);
    if (p.skill === 'recurrence') texts.push(p.hyp, p.init, p.next, ...(p.inst || []));
  });
  texts.forEach((t) => {
    try {
      parseText(t);
    } catch {
      issue('unparsable_line', 'Ligne illisible par le moteur', t);
    }
  });
  if (report.issues.length) return report;

  let st;
  try {
    st = createState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return report;
  }

  // 2. énoncé vrai, pièges faux
  (model.givens || []).concat(model.admitted || []).forEach((g) => {
    if (!isTrue(parseText(g), st))
      issue('given_false', 'Égalité de l’énoncé fausse selon le modèle', g);
  });
  (model.traps || []).forEach((t) => {
    if (isTrue(parseText(t.line), st))
      issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
  });

  // 3. le solveur atteint le but
  const sol = solve(model, opts.prefer ?? null);
  report.reached = sol.reached;
  if (!sol.reached) issue('unsolvable', 'Le moteur ne trouve pas de corrigé qui atteint le but.');

  // 4 + 5. rejouer le corrigé, bout par bout
  const budget = opts.budgetMs ?? 150;
  // Un jugement trop lent est mesuré une 2e fois (le 1er passage dans un outil
  // paie la compilation JIT) : on garde le meilleur des deux.
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    let dt = now() - t0;
    if (dt > budget) {
      const t1 = now();
      f();
      dt = Math.min(dt, now() - t1);
    }
    report.max_judge_ms = Math.max(report.max_judge_ms, dt);
    return out;
  };
  const play = createState(model);
  sol.path.forEach((p) => {
    const atoms = lineAtoms(p.line);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind] && !(v.kind === 'syntax' && OPERATOR.test(atoms[i - 1] || ''))) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          p.text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    const accepted: VerdictKind[] = ['step', 'ready'];
    if (accepted.includes(v.kind)) {
      commit(play, v);
      report.solution.push({ text: p.text, label: p.label, hora: v.hora || 0 });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text: p.text, label: p.label, hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''})`,
        p.text,
      );
    }
  });

  // 5 bis. une feuille où il n'y a rien à faire n'apporte rien (but déjà donné
  // par l'énoncé, ou corrigé sans aucun pas qui rapporte) : on la refuse.
  if (sol.reached && report.max_hora === 0)
    issue(
      'no_steps',
      'Le moteur ne trouve aucun pas à faire : la feuille n’apporterait rien (but déjà donné par l’énoncé ?). Choisis comme but une grandeur à CALCULER.',
    );

  // 6. la réponse ne s'écrit pas d'un bond
  const fresh = createState(model);
  const direct: string[] = [];
  (model.answers || []).forEach((a) => direct.push(a));
  const cong = model.congruence;
  if (cong) {
    const r = fresh.cong!.residue;
    direct.push(`${cong.target} ≡ ${r} [${cong.mod}]`);
  } else if (model.complex?.unknown) {
    // équation en z : toutes les solutions d'un bond
    const u = model.complex.unknown;
    direct.push(fresh.cplx!.sols.map((v) => `${u} = ${cxText(v)}`).join(' ou '));
    if (model.complex.solutions?.length)
      direct.push(model.complex.solutions.map((e) => `${u} = ${e}`).join(' ou '));
  } else if (model.complex) {
    direct.push(`${model.complex.target} = ${cxText(fresh.cplx!.value)}`);
    // la réponse telle que le corrigé l'écrit (« π ÷ 2 », « 2^1013 × i »)
    const last = model.complex.path[model.complex.path.length - 1]!.split('=').pop()!.trim();
    direct.push(`${model.complex.target} = ${last}`);
  } else if (!model.answers) {
    const exact = model.goalValues ? [] : model.goal.flatMap((g) => model.symbols[g]?.exact || []);
    const values = model.goalValues || model.goal.flatMap((g) => model.symbols[g]?.values || []);
    model.goal.forEach((g) =>
      exact.length
        ? exact.forEach((x) => direct.push(`${g} = ${x}`))
        : values.forEach((v) =>
            direct.push(`${g} = ${String(v).replace('.', ',').replace('-', '−')}`),
          ),
    );
  }
  direct.forEach((line) => {
    let atoms: string[];
    try {
      atoms = lineAtoms(parseText(line));
    } catch {
      return;
    }
    const v = judge(fresh, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step' || !isTrue(parseText(line), fresh)) return;
    if (v.rule && CARD_TOOLS.has(v.rule.id)) {
      report.direct_tool = report.direct_tool || v.rule.label;
      return;
    }
    issue('answer_direct', 'La réponse est acceptée sans aucun raisonnement', line);
  });

  // 7. choix du QCM
  (opts.choices || model.choices || []).forEach((c) => {
    if (c.line) {
      let ok: boolean;
      try {
        ok = model.answers ? isAnswer(fresh, c.line) : isTrue(parseText(c.line), fresh);
      } catch {
        issue('choice_inconsistent', `Choix « ${c.label} » illisible`, c.line);
        return;
      }
      if (ok !== c.correct)
        issue(
          'choice_inconsistent',
          c.correct
            ? `La bonne réponse « ${c.label} » n’est pas reconnue`
            : `Le distracteur « ${c.label} » est une réponse correcte selon le modèle`,
          c.line,
        );
      if (!c.correct) countTrap(c.line);
      return;
    }
    // nombres complexes (moteur v11) : la valeur de la cible, i compris (« 16i »)
    if (model.complex) {
      const v =
        c.exact !== undefined
          ? complexValueOf(fresh.cplx!, c.exact)
          : c.value !== undefined
            ? ([c.value, 0] as [number, number])
            : null;
      if (!v) return;
      // équation à plusieurs solutions : le choix (« 1 + i et 1 − i ») n'est pas vérifié ici
      if (model.complex.unknown && fresh.cplx!.sols.length !== 1) return;
      const ok = complexEquals(v, fresh.cplx!.value);
      if (ok !== c.correct)
        issue(
          'choice_inconsistent',
          c.correct
            ? `La bonne réponse « ${c.label} » ne vaut pas ${cxText(fresh.cplx!.value)}`
            : `Le distracteur « ${c.label} » vaut la bonne réponse`,
        );
      return;
    }
    if (c.value === undefined) return;
    if (cong) {
      // congruences : la bonne réponse est LE reste, entre 0 et n − 1 (15 ≡ 1 [7] n'est pas « 1 »)
      const ok = c.value === fresh.cong!.residue;
      if (ok !== c.correct)
        issue(
          'choice_inconsistent',
          c.correct
            ? `La bonne réponse « ${c.label} » n’est pas le reste ${fresh.cong!.residue}`
            : `Le distracteur « ${c.label} » est le reste attendu`,
        );
      return;
    }
    // réponse arrondie (moteur v10) : la bonne réponse est l'ARRONDI de la valeur exacte
    if (model.rounding && c.exact === undefined) {
      const r = model.rounding;
      const g = r.symbol ?? model.goal[0]!;
      const ok = (model.symbols[g]?.values ?? []).some((v) =>
        close(roundTo(v, r.places), c.value!),
      );
      if (ok !== c.correct)
        issue(
          'choice_inconsistent',
          c.correct
            ? `La bonne réponse « ${c.label} » n’est pas l’arrondi attendu`
            : `Le distracteur « ${c.label} » est l’arrondi attendu`,
        );
      return;
    }
    const written = c.exact ?? numText(c.value!);
    const truthy = model.goal.some((g) => {
      const line = `${g} = ${written}`;
      return isTrue(parseText(line), fresh);
    });
    if (truthy !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne vérifie pas le modèle`
          : `Le distracteur « ${c.label} » vérifie le modèle`,
      );
    if (!c.correct) countTrap(`${model.goal[0]} = ${written}`);
  });
  function countTrap(line: string): void {
    try {
      const v = judge(createState(model), chipsOf(lineAtoms(parseText(line))), { verify: true });
      if (v.kind === 'trap') report.distractors_as_traps += 1;
    } catch {
      /* ligne illisible : déjà signalée */
    }
  }

  // 8. budget
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );

  // mode spécial passé au noyau (moteur v34) : la réponse de `kernel`
  if (special && !report.issues.length) checkKernelSpecialAnswer(model, issue);
  report.ok = report.issues.length === 0;
  return report;
}

/** la cible et le corrigé de chaque mode spécial */
function specialSpec(model: ScratchModel): { target: string; path: string[] } {
  if (model.units) return { target: model.units.target, path: model.units.path };
  if (model.congruence) return { target: model.congruence.target, path: model.congruence.path };
  if (model.complex) return { target: model.complex.target, path: model.complex.path };
  return { target: model.bounds!.expr, path: model.bounds!.path };
}

/**
 * Mode spécial passé au noyau (moteur v34) : `kernel` reprend le départ (la cible du
 * mode) et le corrigé du mode, à l'identique.
 */
function checkKernelSpecial(
  model: ScratchModel,
  plugin: string,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
): void {
  const spec = model.kernel!;
  const mode = specialSpec(model);
  if (spec.start.trim() !== mode.target.trim())
    issue('schema', `kernel.start doit être la cible du mode ${plugin} (« ${mode.target} »)`);
  const path = spec.path ?? [];
  if (path.length !== mode.path.length || path.some((t, i) => t.trim() !== mode.path[i]!.trim()))
    issue('schema', `kernel.path doit être le corrigé du mode ${plugin}, à l’identique`);
}

/** La réponse de `kernel` est reconnue une fois le corrigé rejoué. */
function checkKernelSpecialAnswer(
  model: ScratchModel,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
): void {
  const play = createState(model);
  for (const text of model.kernel!.path ?? []) {
    let atoms: string[];
    try {
      atoms = model.units ? text.split(/\s+/).filter(Boolean) : lineAtoms(parseText(text));
    } catch {
      atoms = text.split(/\s+/).filter(Boolean);
    }
    const v = judge(play, chipsOf(atoms), { verify: true });
    if (v.kind === 'step' || v.kind === 'useless') commit(play, v);
  }
  let ok = false;
  try {
    ok = isAnswer(play, model.kernel!.answer);
  } catch {
    ok = false;
  }
  if (!ok)
    issue(
      'choice_inconsistent',
      'La réponse de la feuille (kernel.answer) n’est pas reconnue une fois le corrigé rejoué',
      model.kernel!.answer,
    );
}

/**
 * Porte d'une feuille du noyau (moteur v18) : lignes lisibles par le noyau, grandeurs
 * déclarées, réponse équivalente au départ sous les hypothèses, pièges faux, corrigé
 * de référence rejoué ligne à ligne (et bout par bout) jusqu'à la réponse, réponse
 * jamais acceptée d'un bond, choix du QCM cohérents, budget.
 */
function validateKernel(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  if (spec.plugin === 'geometry') return validateKernelGeometry(model, report, issue, opts);
  if (spec.plugin === 'python') return validateKernelPython(model, report, issue, opts);
  if (spec.plugin === 'matrix') return validateKernelMatrix(model, report, issue, opts);
  if (spec.plugin === 'arith') return validateKernelArith(model, report, issue, opts);
  if (spec.plugin === 'limit') return validateKernelLimit(model, report, issue, opts);
  if (spec.plugin === 'limclaims') return validateKernelLimitClaims(model, report, issue, opts);
  if (spec.plugin === 'integral') return validateKernelIntegral(model, report, issue, opts);
  if (spec.plugin === 'ode') return validateKernelOde(model, report, issue, opts);
  if (spec.plugin === 'partial') return validateKernelPartial(model, report, issue, opts);
  if (spec.plugin === 'riemann') return validateKernelRiemann(model, report, issue, opts);
  if (spec.plugin === 'claims') return validateKernelClaims(model, report, issue, opts);
  if (spec.plugin === 'variations') return validateKernelVariations(model, report, issue, opts);
  if (spec.plugin === 'tables') return validateKernelTables(model, report, issue, opts);
  if (spec.plugin === 'analysis') return validateKernelAnalysis(model, report, issue, opts);
  if (spec.plugin === 'formula') return validateKernelFormula(model, report, issue, opts);
  if (spec.induction) return validateKernelRecurrence(model, report, issue, opts);
  const x = spec.goal.target;
  // 1. lignes lisibles par le noyau, grandeurs déclarées
  const relations = [
    spec.start,
    spec.answer,
    ...(spec.system?.equations ?? []),
    ...(spec.path ?? []),
    ...(spec.conditions ?? []),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const letters = new Set<string>();
  relations.forEach((t) => {
    // tableau de signes (moteur v42) : vérifié par le juge quand le corrigé est rejoué
    if (isSignTableText(t)) return;
    // seuil par tests (moteur v48) : « 1000 × 0,8^11 ≈ 85,9 », un rang testé (le juge le vérifie)
    if (spec.method === 'test' && t.includes('≈')) {
      let ap: ReturnType<typeof approximationOf> = null;
      try {
        ap = approximationOf(parseText(t));
      } catch {
        ap = null;
      }
      if (!ap) issue('unparsable_line', 'Test illisible : « expression ≈ nombre » est attendu', t);
      return;
    }
    // réponse arrondie (moteur v20) : « AC ≈ 7,8 », l'inconnue arrondie
    if (model.rounding && t.includes('≈')) {
      const ap = (() => {
        try {
          return approximationOf(parseText(t));
        } catch {
          return null;
        }
      })();
      if (!ap || ap.l.k !== 'sym' || ap.l.n !== x)
        issue('unparsable_line', `Valeur arrondie illisible : « ${x} ≈ nombre » est attendu`, t);
      return;
    }
    const p = parseStatement(t);
    if (!p.ok || p.s.k === 'expr') {
      issue('unparsable_line', 'Ligne illisible par le noyau (une relation est attendue)', t);
      return;
    }
    statementSymbols(p.s).forEach((n) => letters.add(n));
  });
  (spec.hypotheses ?? []).forEach((h) => {
    const p = parseStatement(h.of);
    if (!p.ok || p.s.k !== 'expr')
      issue('unparsable_line', 'Hypothèse illisible : un terme est attendu (« m − 1 »)', h.of);
    else statementSymbols(p.s).forEach((n) => letters.add(n));
  });
  if (report.issues.length) return;
  const startStmt = parseStatement(spec.start);
  if (spec.system) validateSystemShape(spec.system, spec.start, issue);
  else if (startStmt.ok && !statementSymbols(startStmt.s).has(x))
    issue('schema', `La ligne de départ ne contient pas l’inconnue ${x}`, spec.start);
  // second degré, produit (moteur v23) : sans la méthode « factor », le noyau mesurerait
  // l'avancée à l'inconnue isolée, ce qui n'a pas de sens ici
  else if (
    startStmt.ok &&
    startStmt.s.k === 'rel' &&
    !usesKernelPoly(model) &&
    ['expanded', 'unsorted', 'factored', 'partial'].includes(relForm(startStmt.s, x))
  )
    issue(
      'schema',
      'Second degré, produit ou quotient : ajoute "method": "factor" à la clé "kernel".',
      spec.start,
    );
  // équation trigonométrique en radians (moteur v24) : sans la méthode « trig », le noyau
  // ne saurait ni écrire les familles ni lire l'intervalle
  else if (
    startStmt.ok &&
    !usesKernelTrig(model) &&
    !(spec.hypotheses ?? []).some((h) => h.kind === 'acute' || h.kind === 'angle') &&
    [...statementSymbols(startStmt.s)].length > 0 &&
    relationsIn(startStmt.s).some((r) => trigNodes(r.l, x).length + trigNodes(r.r, x).length > 0)
  )
    issue(
      'schema',
      'Équation trigonométrique : ajoute "method": "trig" à la clé "kernel" (et "domain" pour un intervalle).',
      spec.start,
    );
  if (spec.domain) {
    const d = parseStatement(spec.domain);
    const dom = d.ok ? trigDomainOf(d.s, x) : null;
    if (!dom || dom.lo === null || dom.hi === null)
      issue(
        'unparsable_line',
        `Intervalle illisible : une chaîne comme « −π < ${x} ≤ π », bornes multiples de π`,
        spec.domain,
      );
  }
  [...letters]
    .filter((n) => !model.symbols[n])
    .forEach((n) => issue('schema', `Grandeur ${n} absente de "symbols" (bout de la palette)`));
  if (report.issues.length) return;

  let st;
  try {
    st = createState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const ks = st.kern!;
  // 2. la réponse est équivalente au départ ; les pièges ne le sont pas
  if (spec.system) {
    // système (moteur v22) : la réponse découle des équations, les pièges non
    if (!kernelEquivalentToAnswer(ks, spec.answer))
      issue(
        'unsolvable',
        spec.system.outcome === 'none'
          ? 'La réponse d’un système sans solution est une contradiction qui découle des équations (« 0 = 1 »).'
          : 'La réponse ne découle pas des équations du système.',
        spec.answer,
      );
    if (!spec.path?.length)
      issue('unsolvable', 'Un système exige son corrigé de référence dans "kernel.path".');
    (model.traps ?? []).forEach((t) => {
      if (kernelSystemFollows(ks, t.line))
        issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
    });
  } else {
    if (
      !kernelEquivalentToAnswer(ks, spec.start) &&
      !kernelPathEquivalent(ks, spec.start, spec.path ?? []) &&
      !kernelAnswerFollowsWithConditions(ks)
    )
      issue(
        'unsolvable',
        'La réponse n’est pas équivalente à la ligne de départ (avec les hypothèses déclarées).',
        spec.answer,
      );
    (model.traps ?? []).forEach((t) => {
      if (kernelEquivalentToAnswer(ks, t.line, true))
        issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
    });
    // moteur v49 : une condition découle du départ, en l'inconnue seule, sans le remplacer
    (spec.conditions ?? []).forEach((t) => {
      if (!kernelConditionFollows(ks, t))
        issue(
          'schema',
          `Une condition doit découler de la ligne de départ, en ${x} seul, sans lui être équivalente`,
          t,
        );
    });
  }

  // 3-5. corrigé de référence rejoué, bout par bout
  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    let dt = now() - t0;
    if (dt > budget) {
      const t1 = now();
      f();
      dt = Math.min(dt, now() - t1);
    }
    report.max_judge_ms = Math.max(report.max_judge_ms, dt);
    return out;
  };
  const sol = solve(model);
  if (!sol.path.length)
    issue('unsolvable', 'Le noyau ne trouve pas de corrigé : écris-le dans "kernel.path".');
  const play = createState(model);
  sol.path.forEach((p) => {
    let atoms: string[];
    try {
      atoms = kernelLineAtoms(p.text);
    } catch {
      issue('unparsable_line', 'Ligne du corrigé illisible', p.text);
      return;
    }
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind] && !(v.kind === 'syntax' && OPERATOR.test(atoms[i - 1] || ''))) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          p.text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text: p.text,
        label: v.rule?.label ?? p.label,
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text: p.text, label: p.label, hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''})`,
        p.text,
      );
    }
  });
  report.reached = goalReached(play);
  if (sol.path.length && !report.reached)
    issue('unsolvable', 'Le corrigé n’arrive pas à la réponse (inconnue seule, calcul fini).');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');

  // 6. la réponse ne s'écrit pas d'un bond — sauf une écriture à simplifier (moteur v20) :
  // le bond rapporte un seul pas, le corrigé en rapporte plusieurs
  // (un système sans solution, ou avec une infinité, se conclut souvent en UNE combinaison)
  if (spec.goal.kind !== 'simplify' && (!spec.system || spec.system.outcome === 'unique'))
    try {
      const v = judge(createState(model), chipsOf(kernelLineAtoms(spec.answer)), { verify: true });
      // une seule opération du cours (diviser par 2, multiplier par t) : la question reste
      // ouverte, la feuille juge cette opération et ses pièges (le sens qui change)
      if (v.kind === 'step' && v.rule && !v.rule.id.startsWith('kernel.'))
        report.direct_tool = v.rule.label;
      else if (v.kind === 'step')
        issue('answer_direct', 'La réponse est acceptée sans aucun raisonnement', spec.answer);
    } catch {
      /* réponse illisible : déjà signalée */
    }

  // 7. choix du QCM ; à plusieurs bonnes réponses (lot 6.7), un point « x = v » est juste
  // s'il est UNE des solutions de la réponse (« x = 0 » et « x = 1 » pour « x ≤ 1 »)
  const choices = opts.choices || model.choices || [];
  const several = choices.filter((c) => c.correct).length > 1;
  choices.forEach((c) => {
    const text =
      c.line ??
      (c.exact !== undefined
        ? `${x} = ${c.exact}`
        : c.value !== undefined
          ? `${x} = ${numText(c.value)}`
          : null);
    if (!text) return;
    const ok =
      kernelEquivalentToAnswer(ks, text, true) || (several && kernelAnswerContains(ks, text));
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne dit pas la même chose que la réponse attendue`
          : `Le distracteur « ${c.label} » dit la même chose que la réponse attendue`,
        text,
      );
  });

  // 8. budget
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Démonstration de géométrie (moteur v25) : lignes lisibles, énoncé et réponse VRAIS sur
 * la figure, pièges FAUX, corrigé rejoué bout par bout (jamais rouge en cours de frappe),
 * chaque ligne un pas, la dernière la réponse ; choix du QCM cohérents.
 */
function validateKernelGeometry(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  // 1. lignes lisibles (points de la figure, propriétés ou longueurs)
  const points = new Set(Object.keys(spec.figure!.points));
  const read = (t: string, what: string): boolean => {
    try {
      kgeoReadFact(t, points);
      return true;
    } catch (e) {
      issue('unparsable_line', `${what} illisible : ${e instanceof Error ? e.message : e}`, t);
      return false;
    }
  };
  const facts = [spec.start, ...(model.givens ?? [])];
  facts.forEach((t) => read(t, 'Donnée'));
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (report.issues.length) return;
  let st;
  try {
    st = createState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const ks = st.kgeo!;
  // 2. la figure réalise l'énoncé et la réponse ; les pièges y sont faux
  facts.forEach((t) => {
    if (!kgeoHolds(ks, t)) issue('given_false', 'Donnée fausse sur la figure', t);
  });
  if (!kgeoHolds(ks, spec.answer))
    issue('unsolvable', 'La réponse est fausse sur la figure', spec.answer);
  (model.traps ?? []).forEach((t) => {
    if (kgeoHolds(ks, t.line)) issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
  });
  if (report.issues.length) return;

  // 3. corrigé rejoué bout par bout
  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kgeoLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');

  // 4. la réponse d'un bond : une seule propriété du cours (elle reste à trouver), jamais un calcul
  try {
    const v = judge(createState(model), chipsOf(kgeoLineAtoms(spec.answer)), { verify: true });
    if (v.kind === 'step' && v.rule && !v.rule.id.startsWith('kernel.'))
      report.direct_tool = v.rule.label;
    else if (v.kind === 'step')
      issue('answer_direct', 'La réponse est acceptée sans aucun raisonnement', spec.answer);
  } catch {
    /* réponse illisible : déjà signalée */
  }

  // 5. choix du QCM
  (opts.choices || model.choices || []).forEach((c) => {
    const text =
      c.line ?? (c.value !== undefined ? `${spec.goal.target} = ${numText(c.value)}` : null);
    if (!text) return;
    const ok = kgeoSameAsAnswer(ks, text);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne dit pas la même chose que la réponse attendue`
          : `Le distracteur « ${c.label} » dit la même chose que la réponse attendue`,
        text,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Trace Python (moteur v28) : le programme s'exécute (interpréteur exact, budget) ; les
 * lignes se lisent ; la réponse est l'état à la fin du programme ; les pièges ne sont
 * jamais un état du programme ; la trace de référence rejouée bout par bout, chaque
 * ligne un pas, jusqu'à la conclusion ; au moins deux lignes ; choix du QCM cohérents.
 */
function validateKernelPython(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: KProgState;
  try {
    ks = createKProgState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const complete = ks.mode === 'complete';
  const atomsOf = complete ? kprogCodeAtoms : kprogLineAtoms;
  const read = (t: string, what: string): boolean => {
    try {
      atomsOf(t);
      return true;
    } catch (e) {
      issue('unparsable_line', `${what} illisible : ${e instanceof Error ? e.message : e}`, t);
      return false;
    }
  };
  // random (moteur v29) : une trace a ses tirages imposés, et tous servent
  if (!complete && /\brandom\b/.test(ks.code) && /\bimport\b/.test(ks.code)) {
    if (!ks.draws)
      issue(
        'schema',
        'Un programme avec random se trace avec des tirages imposés par l’énoncé (`program.draws`)',
      );
    else if (ks.trace.drawsUsed < ks.draws.length)
      issue('schema', `Des tirages ne servent pas : le programme en prend ${ks.trace.drawsUsed}`);
  }
  // compléter (moteur v29) : sans la ligne (« pass »), les tests ne passent pas
  if (complete && kprogHolds(ks, 'pass'))
    issue('schema', 'Les tests passent sans la ligne manquante : ils ne vérifient rien');
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne de la trace'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (report.issues.length) return;
  if (!kprogHolds(ks, spec.answer))
    issue(
      'unsolvable',
      complete
        ? 'Avec la ligne attendue, le programme ne passe pas ses tests'
        : 'La réponse n’est pas l’état du programme à la fin',
      spec.answer,
    );
  (model.traps ?? []).forEach((t) => {
    if (kprogEverHolds(ks, t.line))
      issue(
        'trap_true',
        complete
          ? 'Un piège ne doit pas passer les tests'
          : 'Un piège ne doit jamais être un état du programme',
        t.line,
      );
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = atomsOf(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne de la trace refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'La trace n’arrive pas à la conclusion.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  if (!complete && (spec.path ?? []).length < 2)
    issue('answer_direct', 'Une trace d’une seule ligne : rien à suivre pas à pas', spec.answer);

  // choix du QCM : une ligne « c = 2 » qui dit (ou non) la réponse
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kprogSameAsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne dit pas la même chose que la réponse attendue`
          : `Le distracteur « ${c.label} » dit la même chose que la réponse attendue`,
        c.line,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Calcul matriciel (moteur v30) : les matrices de l'énoncé se calculent (tailles), les
 * lignes se lisent ; la réponse est le bon résultat, les pièges sont faux ; le corrigé
 * rejoué bout par bout, chaque ligne un pas, jusqu'à la réponse ; la réponse jamais
 * acceptée d'un bond ; choix du QCM cohérents.
 */
function validateKernelMatrix(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKMatState>;
  try {
    ks = createKMatState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const read = (t: string, what: string): boolean => {
    try {
      parseMatLine(kmatLineAtoms(t));
      return true;
    } catch (e) {
      issue('unparsable_line', `${what} illisible : ${e instanceof Error ? e.message : e}`, t);
      return false;
    }
  };
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (report.issues.length) return;
  if (!kmatIsAnswer(ks, spec.answer))
    issue('unsolvable', 'La réponse n’est pas le résultat du calcul demandé', spec.answer);
  (model.traps ?? []).forEach((t) => {
    if (kmatHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  // la réponse d'un bond, depuis l'énoncé seul : la feuille doit rapporter plus pas à pas
  const fresh = createKMatState(model);
  const direct = kmatJudge(fresh, chipsOf(kmatLineAtoms(spec.answer)), { verify: true });

  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kmatLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  if (direct.kind === 'step' && (direct.hora ?? 0) >= report.max_hora)
    issue(
      'answer_direct',
      'La réponse s’écrit d’un bond et rapporte autant que le corrigé : rien à calculer pas à pas',
      spec.answer,
    );

  // choix du QCM : une ligne « AB = [4, 1 ; 8, 8] » qui donne (ou non) la réponse
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kmatIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas le résultat attendu`
          : `Le distracteur « ${c.label} » donne le résultat attendu`,
        c.line,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Arithmétique (moteur v35) : la question se lit (« PGCD(a ; b) », « au + bv = d » avec
 * d = PGCD(|a| ; |b|)) et demande au moins deux divisions ; la réponse est le PGCD (ou un
 * couple solution), les pièges sont faux ; le corrigé rejoué bout par bout, chaque ligne
 * un pas, jusqu'à la réponse ; la réponse jamais acceptée d'un bond ; choix du QCM
 * cohérents (un distracteur de Bézout ne vérifie pas l'équation).
 */
function validateKernelArith(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let q: ReturnType<typeof arithQuestionOf>;
  try {
    q = arithQuestionOf(model);
  } catch (e) {
    issue(
      'unparsable_line',
      'Départ illisible : ' + (e instanceof Error ? e.message : e),
      spec.start,
    );
    return;
  }
  const goalOf = { gcd: 'gcd', gcd_primes: 'gcd', lcm: 'lcm', bezout: 'bezout' } as const;
  if (goalOf[spec.goal.kind as keyof typeof goalOf] !== q.goal) {
    issue('schema', 'L’objectif ne correspond pas au départ');
    return;
  }
  if (q.a === 0n || q.b === 0n || q.a === q.b) {
    issue('schema', 'Deux entiers non nuls et différents', spec.start);
    return;
  }
  // moteur v51 : par les décompositions, des nombres à décomposer (2 à 100 000)
  if (q.primes && (q.a < 2n || q.b < 2n || q.a > 100000n || q.b > 100000n)) {
    issue('schema', 'Deux entiers entre 2 et 100 000 à décomposer', spec.start);
    return;
  }
  const eu = euclid(q.a, q.b);
  if (!q.primes && eu.m < 2) {
    issue(
      'schema',
      'Le plus petit nombre divise l’autre : pas d’algorithme à dérouler',
      spec.start,
    );
    return;
  }
  if (!q.primes && eu.m > 8)
    issue('schema', 'Plus de 8 divisions : la feuille serait trop longue', spec.start);
  if (q.eq && q.eq.rhs !== eu.r[eu.m])
    issue('schema', 'Le second membre doit être le PGCD des deux coefficients', spec.start);
  let ks: ReturnType<typeof createKArithState>;
  try {
    ks = createKArithState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const read = (t: string, what: string): boolean => {
    try {
      parseArithLine(karithLineAtoms(t));
      return true;
    } catch (e) {
      issue('unparsable_line', `${what} illisible : ${e instanceof Error ? e.message : e}`, t);
      return false;
    }
  };
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (report.issues.length) return;
  if (!karithIsAnswer(ks, spec.answer))
    issue(
      'unsolvable',
      'La réponse n’est pas le PGCD, le PPCM ou un couple solution de la question',
      spec.answer,
    );
  (model.traps ?? []).forEach((t) => {
    if (karithHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const fresh = createKArithState(model);
  const direct = karithJudge(fresh, chipsOf(karithLineAtoms(spec.answer)), { verify: true });

  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = karithLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  if (direct.kind === 'step' && (direct.hora ?? 0) >= report.max_hora)
    issue(
      'answer_direct',
      'La réponse s’écrit d’un bond et rapporte autant que le corrigé : rien à calculer pas à pas',
      spec.answer,
    );

  // choix du QCM : une ligne « PGCD(1071 ; 462) = 21 », « u = −2 ; v = 7 »
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = karithIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas la réponse attendue`
          : `Le distracteur « ${c.label} » est juste lui aussi`,
        c.line,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Limites (moteur v54) : l'expression et le point lisibles, la réponse est la vraie limite,
 * pièges faux, corrigé rejoué bout par bout (chaque ligne un pas) jusqu'à la limite de
 * l'expression ; chaque choix « expression → limite » est juste exactement quand il est la
 * bonne réponse.
 */
function validateKernelLimit(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKLimState>;
  try {
    ks = createKLimState(model);
  } catch (e) {
    issue(
      'unparsable_line',
      'Départ illisible : ' + (e instanceof Error ? e.message : e),
      spec.start,
    );
    return;
  }
  if (ks.equiv) {
    // v61 : un équivalent — la réponse doit être vraie
    if (!klimHolds(ks, spec.answer))
      issue('unsolvable', 'La réponse n’est pas un équivalent de l’expression', spec.answer);
  } else {
    const truth = trueLimit(ks.target, ks.P).v;
    if (!truth) {
      issue('unsolvable', 'On ne sait pas vérifier la limite de l’expression', spec.start);
      return;
    }
    if (!sameLimit(ks.answer, truth))
      issue('unsolvable', 'La réponse n’est pas la limite de l’expression', spec.answer);
  }
  (model.traps ?? []).forEach((t) => {
    if (klimHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = klimLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached)
    issue(
      'unsolvable',
      ks.equiv
        ? 'Le corrigé n’arrive pas à l’équivalent de la réponse.'
        : 'Le corrigé n’arrive pas à la limite de l’expression.',
    );
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = klimIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas la limite attendue`
          : `Le distracteur « ${c.label} » est juste lui aussi`,
        c.line,
      );
  });
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Limites affirmées (lot 6.62, moteur v57) : chaque expression et chaque point se lisent,
 * la vraie limite de chaque expression est connue (sauf, avec `single`, pour celles que
 * l'élimination tranche) ; la vérité de chaque affirmation est celle du QCM ; `answer`
 * liste les vraies ; corrigé rejoué bout par bout, chaque ligne un pas, jusqu'à ce que
 * tout soit tranché.
 */
function validateKernelLimitClaims(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: KLcState;
  try {
    ks = createKLcState(model);
  } catch (e) {
    issue(
      e instanceof LimitClaimsModelError ? 'unparsable_line' : 'schema',
      'Limites affirmées injouables : ' + (e instanceof Error ? e.message : String(e)),
    );
    return;
  }
  const choices = opts.choices || model.choices || [];
  if (choices.length !== ks.claims.length)
    issue('schema', `${ks.claims.length} affirmations pour ${choices.length} choix`);
  ks.claims.forEach((c, i) => {
    const ch = choices[i];
    if (c.truth === null && !spec.single)
      issue('unsolvable', 'On ne sait pas vérifier la limite de cette expression', c.spec.expr);
    if (!ch || c.truth === null || ch.correct === c.truth) return;
    issue(
      'choice_inconsistent',
      ch.correct
        ? `La bonne réponse « ${ch.label} » : sa limite n’est pas celle affirmée`
        : `Le distracteur « ${ch.label} » : sa limite est bien celle affirmée`,
      c.spec.expr,
    );
  });
  const trues = ks.claims.filter((c) => c.truth === true).length;
  const letters = ks.claims
    .flatMap((c, i) => (choices[i]?.correct ? [String.fromCharCode(65 + i)] : []))
    .join(', ');
  if (spec.answer.replace(/\s+/g, '') !== letters.replace(/\s+/g, ''))
    issue('schema', `\`answer\` liste les affirmations vraies : « ${letters} »`, spec.answer);
  if (spec.single && trues !== 1)
    issue('schema', '"single": une seule affirmation doit être vraie', spec.answer);
  if (!trues) issue('schema', 'Au moins une affirmation vraie');
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = klimLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending' || v.kind === 'ready')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé ne tranche pas tous les choix.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  (model.traps ?? []).forEach((t) => {
    if (klcHoldsText(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Intégrales (moteur v55) : l'intégrale lisible et calculable, la réponse est sa valeur,
 * pièges faux, corrigé rejoué bout par bout (chaque ligne un pas) jusqu'à la valeur ;
 * chaque choix « I = valeur » est juste exactement quand il est la bonne réponse.
 */
/**
 * Sommes de Riemann (moteur v59) : départ et réponse lisibles, la réponse est la limite de
 * la suite (somme de Riemann de l'énoncé, ou valeurs pour de grands n pour un produit), les
 * pièges sont faux, le corrigé est rejoué ligne à ligne (en cours de frappe : jamais rouge)
 * et atteint la réponse, les choix du QCM sont cohérents.
 */
function validateKernelRiemann(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKRiemState>;
  try {
    ks = createKRiemState(model);
  } catch (e) {
    issue(
      'unparsable_line',
      'Départ illisible : ' + (e instanceof Error ? e.message : e),
      spec.start,
    );
    return;
  }
  const lim = kriemCheckLimit(model);
  const tol = lim.how === 'riemann' ? 1e-7 : 2e-3;
  if (
    !Number.isFinite(lim.value) ||
    !(Math.abs(lim.value - ks.answerValue) <= tol * Math.max(1, Math.abs(ks.answerValue)))
  )
    issue('unsolvable', 'La réponse n’est pas la limite de la suite', spec.answer);
  (model.traps ?? []).forEach((t) => {
    if (kriemHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kriemLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la limite.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kriemIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas la limite attendue`
          : `Le distracteur « ${c.label} » est juste lui aussi`,
        c.line,
      );
  });
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

function validateKernelIntegral(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKIntState>;
  try {
    ks = createKIntState(model);
  } catch (e) {
    issue(
      'unparsable_line',
      'Départ illisible : ' + (e instanceof Error ? e.message : e),
      spec.start,
    );
    return;
  }
  if (!(Math.abs(ks.answerValue - ks.value) <= 1e-9 * Math.max(1, Math.abs(ks.value))))
    issue('unsolvable', 'La réponse n’est pas la valeur de l’intégrale', spec.answer);
  (model.traps ?? []).forEach((t) => {
    if (kintHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kintLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la valeur de l’intégrale.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kintIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas la valeur attendue`
          : `Le distracteur « ${c.label} » est juste lui aussi`,
        c.line,
      );
  });
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Équation différentielle (moteur v62) : l'équation et la réponse se lisent (la réponse est
 * juste : l'équation réécrite, toutes les solutions, la solution des conditions) ; pièges
 * faux ; corrigé rejoué bout par bout, chaque ligne un pas, jusqu'à la réponse ; un choix
 * est juste si et seulement si sa ligne est la réponse.
 */
function validateKernelOde(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKOdeState>;
  try {
    ks = createKOdeState(model);
  } catch (e) {
    issue(
      'unparsable_line',
      'Modèle illisible : ' + (e instanceof Error ? e.message : e),
      spec.start,
    );
    return;
  }
  (model.traps ?? []).forEach((t) => {
    if (kodeHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kodeLineAtoms(text, ks.fn, ks.x);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kodeIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas la réponse attendue`
          : `Le distracteur « ${c.label} » est juste lui aussi`,
        c.line,
      );
  });
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Dérivées partielles (moteur v64) : la fonction se lit, la réponse est juste, les pièges
 * sont faux, le corrigé est rejoué pas à pas jusqu'à la réponse, chaque choix du QCM est
 * tranché comme le QCM.
 */
function validateKernelPartial(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKPartState>;
  try {
    ks = createKPartState(model);
  } catch (e) {
    issue(
      'unparsable_line',
      'Modèle illisible : ' + (e instanceof Error ? e.message : e),
      spec.start,
    );
    return;
  }
  (model.traps ?? []).forEach((t) => {
    if (kpartHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kpartLineAtoms(text, ks.spec);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kpartIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne donne pas la réponse attendue`
          : `Le distracteur « ${c.label} » est juste lui aussi`,
        c.line,
      );
  });
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Affirmations à trancher (moteur v36) : une affirmation par choix du QCM, chacune lisible
 * et décidable exactement ; la vérité de chacune est celle du QCM (vraie ⇔ bonne réponse) ;
 * `answer` les liste (« A, C ») ; pièges faux ; corrigé rejoué bout par bout, chaque ligne
 * un pas, jusqu'à ce que toutes soient tranchées.
 */
function validateKernelClaims(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKClaimsState>;
  try {
    ks = createKClaimsState(model);
  } catch (e) {
    issue(
      e instanceof ClaimsModelError ? 'unparsable_line' : 'schema',
      'Affirmation injouable : ' + (e instanceof Error ? e.message : String(e)),
    );
    return;
  }
  const read = (t: string, what: string): boolean => {
    let ok = false;
    try {
      ok = !!kforReadAtoms(kclaimsLineAtoms(t));
    } catch {
      ok = false;
    }
    if (!ok) issue('unparsable_line', `${what} illisible`, t);
    return ok;
  };
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  if (report.issues.length) return;

  // vérité de chaque affirmation : celle du QCM
  const choices = opts.choices || model.choices || [];
  if (choices.length !== ks.claims.length)
    issue('schema', `${ks.claims.length} affirmations pour ${choices.length} choix`);
  ks.claims.forEach((c, i) => {
    const ch = choices[i];
    if (!ch || ch.correct === c.truth) return;
    issue(
      'choice_inconsistent',
      ch.correct
        ? `La bonne réponse « ${ch.label} » : son affirmation est fausse`
        : `Le distracteur « ${ch.label} » : son affirmation est vraie`,
      c.rels.map((r) => r.text).join(' ; '),
    );
  });
  const letters = ks.claims
    .flatMap((c, i) => (c.truth ? [String.fromCharCode(65 + i)] : []))
    .join(', ');
  if (spec.answer.replace(/\s+/g, '') !== letters.replace(/\s+/g, ''))
    issue('schema', `\`answer\` liste les affirmations vraies : « ${letters} »`, spec.answer);
  if (!ks.claims.some((c) => c.truth))
    issue('schema', 'Au moins une affirmation vraie (un QCM « coche les vraies »)');
  // une seule réponse juste (moteur v40) : l'élimination n'a de sens que si c'est vrai
  if (spec.single && ks.claims.filter((c) => c.truth).length !== 1)
    issue('schema', '"single": une seule affirmation doit être vraie', spec.answer);
  (model.traps ?? []).forEach((t) => {
    if (kclaimsHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kclaimsLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé ne tranche pas toutes les affirmations.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Étude de fonction (lot 6.20, moteur v42 étendu) : le modèle se joue (f ou f′ lisible,
 * dérivée et tableau du corrigé) ; une affirmation par choix, sa vérité (lue dans le
 * tableau du corrigé) est celle du QCM ; `answer` liste les vraies ; avec `single`, une
 * seule vraie (les affirmations qui ne se lisent pas dans le tableau sont alors fausses
 * par élimination), sans `single`, toutes se lisent ; corrigé rejoué, chaque ligne un pas,
 * jusqu'à ce que le tableau tranche tout.
 */
function validateKernelVariations(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: KVarState;
  try {
    ks = createKVarState(model);
  } catch (e) {
    issue(
      e instanceof VariationsModelError ? 'unparsable_line' : 'schema',
      'Étude de fonction injouable : ' + (e instanceof Error ? e.message : String(e)),
    );
    return;
  }
  const choices = opts.choices || model.choices || [];
  if (choices.length !== ks.claims.length)
    issue('schema', `${ks.claims.length} affirmations pour ${choices.length} choix`);
  ks.claims.forEach((c, i) => {
    const ch = choices[i];
    if (c.truth === null && !spec.single)
      issue(
        'unsolvable',
        'Sans `single`, chaque affirmation doit se lire dans le tableau',
        c.spec.text,
      );
    if (!ch || c.truth === null || ch.correct === c.truth) return;
    issue(
      'choice_inconsistent',
      ch.correct
        ? `La bonne réponse « ${ch.label} » : son affirmation est fausse d’après le tableau`
        : `Le distracteur « ${ch.label} » : son affirmation est vraie d’après le tableau`,
      c.spec.text,
    );
  });
  const trues = ks.claims.filter((c) => c.truth === true).length;
  const letters = ks.claims
    .flatMap((c, i) => (choices[i]?.correct ? [String.fromCharCode(65 + i)] : []))
    .join(', ');
  if (spec.answer.replace(/\s+/g, '') !== letters.replace(/\s+/g, ''))
    issue('schema', `\`answer\` liste les affirmations vraies : « ${letters} »`, spec.answer);
  if (spec.single && trues !== 1)
    issue('schema', '"single": une seule affirmation doit être vraie', spec.answer);
  if (!trues) issue('schema', 'Au moins une affirmation vraie');
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kvarLineAtoms(text);
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending' || v.kind === 'ready')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé ne tranche pas tous les choix.');
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Tableau à remplir (lot 6.21) : le tableau de l'énoncé se lit, chaque affirmation a sa
 * vérité (cohérente avec le QCM) ; le corrigé rejoué tranche tous les choix.
 */
function validateKernelTables(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: KTabState;
  try {
    ks = createKTabState(model);
  } catch (e) {
    issue(
      e instanceof TablesModelError ? 'unparsable_line' : 'schema',
      'Tableau injouable : ' + (e instanceof Error ? e.message : String(e)),
    );
    return;
  }
  const choices = opts.choices || model.choices || [];
  if (choices.length !== ks.claims.length)
    issue('schema', `${ks.claims.length} affirmations pour ${choices.length} choix`);
  ks.claims.forEach((c, i) => {
    const ch = choices[i];
    if (c.truth === null && !spec.single)
      issue(
        'unsolvable',
        'Sans `single`, chaque affirmation doit se lire dans le tableau',
        c.spec.text,
      );
    if (!ch || c.truth === null || ch.correct === c.truth) return;
    issue(
      'choice_inconsistent',
      ch.correct
        ? `La bonne réponse « ${ch.label} » : son affirmation est fausse d’après le tableau`
        : `Le distracteur « ${ch.label} » : son affirmation est vraie d’après le tableau`,
      c.spec.text,
    );
  });
  const trues = ks.claims.filter((c) => c.truth === true).length;
  const letters = ks.claims
    .flatMap((c, i) => (choices[i]?.correct ? [String.fromCharCode(65 + i)] : []))
    .join(', ');
  if (spec.answer.replace(/\s+/g, '') !== letters.replace(/\s+/g, ''))
    issue('schema', `\`answer\` liste les affirmations vraies : « ${letters} »`, spec.answer);
  if (spec.single && trues !== 1)
    issue('schema', '"single": une seule affirmation doit être vraie', spec.answer);
  if (!trues) issue('schema', 'Au moins une affirmation vraie');
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = ktabLineAtoms(text);
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending' || v.kind === 'ready')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé ne tranche pas tous les choix.');
  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Dérivées et primitives (moteur v31) : le modèle se joue (fonction lisible, assez de points
 * de l'intervalle) ; la réponse est la bonne dérivée (ou une primitive), les pièges sont
 * faux ; corrigé rejoué bout par bout, chaque ligne un pas, jusqu'à la réponse. Une
 * réponse d'un bond n'est acceptée que si le corrigé tient en une ligne (une règle :
 * dérivées usuelles, primitive reconnue) ; choix du QCM cohérents.
 */
function validateKernelAnalysis(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKAnaState>;
  try {
    ks = createKAnaState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const head = (t: string): boolean => {
    const v = kanaJudge(createKAnaState(model), chipsOf(kanaLineAtoms(t)), { verify: true });
    return v.kind !== 'syntax';
  };
  const read = (t: string, what: string): void => {
    if (!head(t)) issue('unparsable_line', `${what} illisible`, t);
  };
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (report.issues.length) return;
  if (!kanaIsAnswer(ks, spec.answer))
    issue(
      'unsolvable',
      spec.goal.kind === 'antiderivative'
        ? 'La réponse n’est pas une primitive de la fonction'
        : 'La réponse n’est pas la dérivée de la fonction',
      spec.answer,
    );
  (model.traps ?? []).forEach((t) => {
    if (kanaHolds(ks, t.line)) issue('trap_true', 'Un piège ne doit pas être juste', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const direct = kanaJudge(createKAnaState(model), chipsOf(kanaLineAtoms(spec.answer)), {
    verify: true,
  });

  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kanaLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');
  // une seule ligne : la règle (dérivées usuelles, primitive reconnue) est ce qu'on juge
  if ((spec.path ?? []).length === 1 && direct.kind === 'step')
    report.direct_tool = direct.rule?.label ?? null;
  else if (direct.kind === 'step' && (direct.hora ?? 0) >= report.max_hora)
    issue(
      'answer_direct',
      'La réponse s’écrit d’un bond et rapporte autant que le corrigé : rien à faire pas à pas',
      spec.answer,
    );

  // choix du QCM : une ligne « f′(x) = … » juste (ou non)
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = kanaIsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » n’est pas juste pour le moteur`
          : `Le distracteur « ${c.label} » est juste pour le moteur`,
        c.line,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Feuille à formules (moteur v32) : chaque grandeur a sa valeur, la réponse est juste,
 * les pièges sont faux ; corrigé rejoué bout par bout, chaque ligne un pas, jusqu'aux
 * grandeurs cherchées ; la réponse d'un bond n'est pas un pas (elle est gardée sans
 * HORA) ; choix du QCM cohérents (valeur, arrondi ou ligne) ; budget.
 */
function validateKernelFormula(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  let ks: ReturnType<typeof createKForState>;
  try {
    ks = createKForState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const readable = (t: string): boolean => {
    try {
      const v = kforJudge(createKForState(model), chipsOf(kforLineAtoms(t)), { verify: true });
      return v.kind !== 'syntax' && v.title !== 'Une relation par ligne';
    } catch {
      return false;
    }
  };
  const read = (t: string, what: string): void => {
    if (!readable(t)) issue('unparsable_line', `${what} illisible`, t);
  };
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (report.issues.length) return;
  if (!kforHolds(ks, spec.answer))
    issue('unsolvable', 'La réponse est fausse selon les valeurs de l’énoncé', spec.answer);
  (model.traps ?? []).forEach((t) => {
    if (kforHolds(ks, t.line)) issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    let dt = now() - t0;
    if (dt > budget) {
      const t1 = now();
      f();
      dt = Math.min(dt, now() - t1);
    }
    report.max_judge_ms = Math.max(report.max_judge_ms, dt);
    return out;
  };

  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = kforLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind] && !(v.kind === 'syntax' && OPERATOR.test(atoms[i - 1] || ''))) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? v.title ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas aux grandeurs cherchées.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');

  // la réponse d'un bond : gardée sans HORA (D7), jamais un pas qui rapporte tout
  const direct = kforJudge(createKForState(model), chipsOf(kforLineAtoms(spec.answer)), {
    verify: true,
  });
  if ((spec.path ?? []).length === 1 && direct.kind === 'step')
    report.direct_tool = direct.rule?.label ?? direct.title ?? null;
  else if (direct.kind === 'step' && (direct.hora ?? 0) >= report.max_hora && report.max_hora > 0)
    issue(
      'answer_direct',
      'La réponse s’écrit d’un bond et rapporte autant que le corrigé : rien à faire pas à pas',
      spec.answer,
    );

  // choix du QCM
  const goal = model.goal[0]!;
  const goalValues = (): number[] => {
    const v = ks.Wf[goal];
    return v === undefined || !Number.isFinite(v) ? [] : [v];
  };
  (opts.choices || model.choices || []).forEach((c) => {
    let ok: boolean | null = null;
    if (c.line) {
      ok = kforIsAnswer(ks, c.line);
    } else if (model.rounding && c.exact === undefined && c.value !== undefined) {
      const r = model.rounding;
      ok = goalValues().some((v) => close(roundTo(v, r.places), c.value!));
    } else if (c.exact !== undefined || c.value !== undefined) {
      const written = c.exact ?? numText(c.value!);
      ok = kforHolds(ks, `${goal} = ${written}`);
    }
    if (ok === null) return;
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » n’est pas juste pour le moteur`
          : `Le distracteur « ${c.label} » est juste pour le moteur`,
        c.line,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Récurrence (moteur v26) : lignes lisibles, grandeurs déclarées ; la réponse P(n + 1)
 * découle de l'énoncé (définition + hypothèse de récurrence), les pièges sont faux ;
 * corrigé rejoué bout par bout, chaque ligne un pas, la dernière la réponse ; la réponse
 * jamais acceptée d'un bond ; choix du QCM cohérents.
 */
function validateKernelRecurrence(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.kernel!;
  const ind = spec.induction!;
  const declared = new Set([...Object.keys(model.symbols), 'π']);
  const read = (t: string, what: string): boolean => {
    try {
      const f = krecReadFact(t);
      const unknown = [
        ...new Set(f.pairs.flatMap((r) => [...symbolsOf(r.l), ...symbolsOf(r.r)])),
      ].filter((n) => !declared.has(n));
      if (unknown.length) {
        issue('unparsable_line', `${what} : grandeur non déclarée (${unknown.join(', ')})`, t);
        return false;
      }
      return true;
    } catch (e) {
      issue('unparsable_line', `${what} illisible : ${e instanceof Error ? e.message : e}`, t);
      return false;
    }
  };
  read(spec.start, 'Définition');
  (model.givens ?? []).forEach((t) => read(t, 'Donnée'));
  read(ind.hypothesis, 'Hypothèse de récurrence');
  read(spec.answer, 'Réponse');
  (spec.path ?? []).forEach((t) => read(t, 'Ligne du corrigé'));
  (model.traps ?? []).forEach((t) => read(t.line, 'Piège'));
  (model.choices ?? []).forEach((c) => {
    if (c.line) read(c.line, 'Choix');
  });
  if (!declared.has(ind.variable ?? 'n'))
    issue('schema', `La variable de la récurrence ${ind.variable ?? 'n'} doit être déclarée`);
  if (report.issues.length) return;
  let ks;
  try {
    ks = createKRecState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  // la réponse découle de l'énoncé ; les pièges sont faux
  const ans = krecTruth(ks, spec.answer);
  if (ans !== 'true')
    issue(
      'unsolvable',
      ans === 'false'
        ? 'La réponse ne découle pas de l’énoncé et de l’hypothèse de récurrence'
        : 'Je ne sais pas vérifier la réponse',
      spec.answer,
    );
  if (krecSameAsAnswer(ks, ind.hypothesis))
    issue('schema', 'L’hypothèse de récurrence est déjà la réponse', ind.hypothesis);
  (model.traps ?? []).forEach((t) => {
    if (krecTruth(ks, t.line) !== 'false')
      issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
  });
  if (report.issues.length) return;

  const budget = opts.budgetMs ?? 150;
  const timed = <T>(f: () => T): T => {
    const t0 = now();
    const out = f();
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    return out;
  };
  const play = createState(model);
  (spec.path ?? []).forEach((text) => {
    const atoms = krecLineAtoms(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = timed(() => judge(play, chipsOf(atoms.slice(0, i))));
      if (RED[v.kind]) {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe${v.title ? ' (' + v.title + ')' : ''}`,
          text,
        );
        break;
      }
    }
    let v = timed(() => judge(play, chipsOf(atoms)));
    if (v.kind === 'building' || v.kind === 'pending')
      v = timed(() => judge(play, chipsOf(atoms), { verify: true }));
    if (v.kind === 'step' || v.kind === 'ready') {
      commit(play, v);
      report.solution.push({
        text,
        label: v.rule?.label ?? '',
        hora: v.hora || 0,
        ...(v.rule ? { rule: v.rule.id } : {}),
      });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’arrive pas à la réponse.');
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Aucun pas ne rapporte de HORA : la feuille n’apporterait rien.');

  // la réponse d'un bond : jamais (l'hérédité se démontre)
  try {
    const v = judge(createState(model), chipsOf(krecLineAtoms(spec.answer)), { verify: true });
    if (v.kind === 'step')
      issue('answer_direct', 'La réponse est acceptée sans raisonnement', spec.answer);
  } catch {
    /* réponse illisible : déjà signalée */
  }

  // choix du QCM
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    const ok = krecSameAsAnswer(ks, c.line);
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne dit pas la même chose que la réponse attendue`
          : `Le distracteur « ${c.label} » dit la même chose que la réponse attendue`,
        c.line,
      );
  });

  if (report.max_judge_ms > budget)
    issue(
      'too_slow',
      `Jugement le plus lent : ${Math.round(report.max_judge_ms)} ms (budget ${budget} ms)`,
    );
}

/**
 * Système (moteur v22) : équations du premier degré, inconnues déclarées et toutes
 * présentes, ligne de départ = une équation, nombre de solutions annoncé exact.
 */
function validateSystemShape(
  sys: NonNullable<NonNullable<ScratchModel['kernel']>['system']>,
  start: string,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
): void {
  const forms = sys.equations.flatMap((t) => {
    const p = parseStatement(t);
    const f = p.ok ? linearForm(p.s) : null;
    if (!f) {
      issue('unparsable_line', 'Équation du système non linéaire (premier degré attendu)', t);
      return [];
    }
    const extra = [...f.coef.keys()].filter((n) => !sys.unknowns.includes(n));
    if (extra.length)
      issue('schema', `Lettre ${extra.join(', ')} hors des inconnues du système`, t);
    return [f];
  });
  if (forms.length !== sys.equations.length) return;
  const seen = new Set(forms.flatMap((f) => [...f.coef.keys()]));
  sys.unknowns
    .filter((u) => !seen.has(u))
    .forEach((u) => issue('schema', `L’inconnue ${u} n’apparaît dans aucune équation`));
  if (!sys.equations.includes(start))
    issue('schema', 'La ligne de départ doit être une des équations du système', start);
  const outcome = systemOutcomeOf(forms, sys.unknowns);
  if (outcome !== sys.outcome) {
    const words = {
      unique: 'une seule solution',
      none: 'aucune solution',
      infinite: 'une infinité de solutions',
    };
    issue(
      'unsolvable',
      `Le système a ${outcome ? words[outcome] : '(trop gros pour être résolu)'}, le modèle annonce ${words[sys.outcome]}.`,
    );
  }
}

/** Porte du mode grandeurs : corrigé rejoué, but atteint, choix exprimés dans l'unité attendue. */
function validateUnits(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.units!;
  let st;
  try {
    st = createState(model);
  } catch (e) {
    issue('schema', 'Modèle injouable : ' + (e instanceof Error ? e.message : String(e)));
    return;
  }
  const us = st.units!;
  const lineOk = (t: string): boolean => {
    try {
      parseUnitsText(t);
      return true;
    } catch {
      issue('unparsable_line', 'Ligne illisible par le moteur', t);
      return false;
    }
  };
  [...(model.givens || []), ...(model.traps || []).map((t) => t.line), ...spec.path].forEach(
    lineOk,
  );
  if (report.issues.length) return;
  (model.traps || []).forEach((t) => {
    if (unitsIsTrue(unitsLineToLine(parseUnitsText(t.line))))
      issue('trap_true', 'Un piège doit être une ligne FAUSSE', t.line);
  });
  const sol = solve(model);
  const play = createState(model);
  sol.path.forEach((p) => {
    const atoms = p.text.split(/\s+/).filter(Boolean);
    for (let i = 1; i < atoms.length; i++) {
      const v = judge(play, chipsOf(atoms.slice(0, i)));
      if (v.kind === 'syntax') {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe (${v.title})`,
          p.text,
        );
        break;
      }
    }
    const t0 = now();
    const v = judge(play, chipsOf(atoms), { verify: true });
    report.max_judge_ms = Math.max(report.max_judge_ms, now() - t0);
    if (v.kind === 'step') {
      commit(play, v);
      report.solution.push({ text: p.text, label: p.label, hora: v.hora || 0 });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text: p.text, label: p.label, hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''}${v.message ? ' — ' + v.message : ''})`,
        p.text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached)
    issue('unsolvable', `Le corrigé n’écrit pas ${spec.target} en ${spec.unit}.`);
  if (report.reached && report.max_hora === 0)
    issue('no_steps', 'Le corrigé ne rapporte aucun HORA.');
  const vu = us.valueUnit;
  (opts.choices || model.choices || []).forEach((c) => {
    if (c.value === undefined) return;
    const ok =
      Math.abs(c.value * vu.f - us.tv.v) <= 1e-9 * Math.max(1, Math.abs(us.tv.v)) &&
      unitsIsTrue(
        unitsLineToLine(parseUnitsText(`${spec.target} = ${numText(c.value)} ${vu.text}`)),
      );
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » ne vaut pas ${spec.target} (en ${vu.text})`
          : `Le distracteur « ${c.label} » vaut ${spec.target}`,
      );
  });
}

/** Porte du mode géométrie : corrigé rejoué (une propriété par ligne), but atteint, choix. */
function validateGeometry(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
  opts: ValidateOptions,
): void {
  const spec = model.geometry!;
  const read = (t: string): boolean => {
    try {
      parseProp(t);
      return true;
    } catch {
      issue('unparsable_line', 'Proposition illisible par le moteur', t);
      return false;
    }
  };
  [
    spec.goal,
    ...(model.givens || []),
    ...(model.traps || []).map((t) => t.line),
    ...spec.path,
  ].forEach(read);
  if (report.issues.length) return;
  const play = createState(model);
  if (goalReached(play)) issue('no_steps', 'La proposition à démontrer est déjà dans l’énoncé.');
  spec.path.forEach((text) => {
    const atoms = geoChipsOf(text);
    for (let i = 1; i < atoms.length; i++) {
      const v = judge(play, chipsOf(atoms.slice(0, i)));
      if (v.kind === 'syntax') {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé syntax en cours de frappe`,
          text,
        );
        break;
      }
    }
    const v = judge(play, chipsOf(atoms), { verify: true });
    if (v.kind === 'step') {
      commit(play, v);
      report.solution.push({ text, label: v.rule?.label ?? '', hora: v.hora || 0 });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', `Le corrigé ne démontre pas « ${spec.goal} ».`);
  (opts.choices || model.choices || []).forEach((c) => {
    if (!c.line) return;
    let ok: boolean;
    try {
      ok = propKey(parseProp(c.line)) === propKey(parseProp(spec.goal));
    } catch {
      issue('choice_inconsistent', `Choix « ${c.label} » illisible`, c.line);
      return;
    }
    if (ok !== c.correct)
      issue(
        'choice_inconsistent',
        c.correct
          ? `La bonne réponse « ${c.label} » n’est pas la proposition à démontrer`
          : `Le distracteur « ${c.label} » est la proposition à démontrer`,
        c.line,
      );
  });
}

/** Porte du mode encadrement : corrigé rejoué ligne à ligne, but atteint, frappe sans rouge. */
function validateBounds(
  model: ScratchModel,
  report: ValidationReport,
  issue: (code: ValidationIssueCode, message: string, line?: string) => void,
): void {
  const spec = model.bounds!;
  const play = createState(model);
  spec.path.forEach((text) => {
    const atoms = normalize(lex(text));
    for (let i = 1; i < atoms.length; i++) {
      const v = judge(play, chipsOf(atoms.slice(0, i)));
      if (v.kind === 'syntax' || v.kind === 'trap') {
        issue(
          'red_while_typing',
          `« ${atoms.slice(0, i).join(' ')} » est jugé ${v.kind} en cours de frappe`,
          text,
        );
        break;
      }
    }
    const v = judge(play, chipsOf(atoms), { verify: true });
    if (v.kind === 'step') {
      commit(play, v);
      report.solution.push({ text, label: v.rule?.label ?? '', hora: v.hora || 0 });
      report.max_hora += v.hora || 0;
    } else {
      report.solution.push({ text, label: '', hora: 0 });
      issue(
        'path_line_rejected',
        `Ligne du corrigé refusée par le juge (${v.kind}${v.title ? ' : ' + v.title : ''})`,
        text,
      );
    }
  });
  report.reached = goalReached(play);
  if (!report.reached) issue('unsolvable', 'Le corrigé n’écrit pas l’encadrement demandé.');
}

function numText(v: number): string {
  if (Number.isInteger(v)) return v < 0 ? '−' + Math.abs(v) : String(v);
  // comme le lexique : un très petit nombre (4 × 10⁻⁹) garde ses chiffres
  return formatNumber(v);
}

/** le modèle brut (avant lecture) écrit-il un produit de lettres nommées ? */
function rawUsesNamedProducts(input: unknown): boolean {
  if (!input || typeof input !== 'object') return false;
  try {
    return usesNamedProducts(input as Parameters<typeof usesNamedProducts>[0]);
  } catch {
    return false;
  }
}
